import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid,
  TextField, Alert, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel, List, ListItem, ListItemText,
  Tooltip, Divider, Chip, Tabs, Tab, Paper,
  Switch, FormControlLabel, LinearProgress
} from '@mui/material';
import AddIcon        from '@mui/icons-material/Add';
import DeleteIcon     from '@mui/icons-material/Delete';
import EditIcon       from '@mui/icons-material/Edit';
import SaveIcon       from '@mui/icons-material/Save';
import MapIcon        from '@mui/icons-material/Map';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RouterIcon     from '@mui/icons-material/Router';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RotateLeftIcon  from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import LayersIcon      from '@mui/icons-material/Layers';
import { sitesAPI, zonesAPI, floorplanAPI } from '../services/api';
import api from '../services/api';


// ─── Geometrie des capteurs ──────────────────────────────────────────────────
// Anisotropie = racine du rapport des valeurs propres de la matrice de
// covariance des positions (ACP). Vaut 1 pour des capteurs disposes en cercle,
// tend vers l'infini quand ils s'alignent.
//   <= 2  : geometrie saine
//   2-3   : acceptable
//   > 3   : positionnement lateral qui se degrade
//   4,9   : valeur mesuree sur SOBR, ou le lateral est perdu
// Une bonne COUVERTURE ne garantit PAS une bonne geometrie : 3 capteurs alignes
// couvrent parfaitement une boutique en longueur et trilaterent tres mal.
function anisotropyOf(points) {
  const pts = (points || []).filter(p => p && (p.x_cm || p.y_cm));
  if (pts.length < 3) return null;
  const n = pts.length;
  const mx = pts.reduce((a, p) => a + p.x_cm, 0) / n;
  const my = pts.reduce((a, p) => a + p.y_cm, 0) / n;
  let sxx = 0, syy = 0, sxy = 0;
  pts.forEach(p => {
    const dx = p.x_cm - mx, dy = p.y_cm - my;
    sxx += dx * dx; syy += dy * dy; sxy += dx * dy;
  });
  sxx /= n; syy /= n; sxy /= n;
  const tr = sxx + syy, det = sxx * syy - sxy * sxy;
  const disc = Math.max(0, tr * tr / 4 - det);
  const l1 = tr / 2 + Math.sqrt(disc);
  const l2 = tr / 2 - Math.sqrt(disc);
  if (l2 <= 1e-6) return 99;
  return Math.sqrt(l1 / l2);
}

// Penalite appliquee au score d'optimisation. Volontairement MODEREE : la
// couverture reste le critere principal, on evite seulement les configurations
// degenerees. L'arbitrage final reste a l'installateur, qui connait les
// contraintes physiques (prises, murs porteurs).
function geoPenalty(aniso) {
  if (aniso === null) return 1;
  if (aniso <= 2) return 1;
  if (aniso >= 5) return 0.6;
  return 1 - 0.4 * (aniso - 2) / 3;
}

const COLORS = ['#3498DB','#E91E63','#4CAF50','#FF9800','#9C27B0','#F44336','#00BCD4','#795548','#607D8B','#FFEB3B'];
const EMPTY_FORM = { name: '', color: '#3498DB', display_order: '' };
const CANVAS_W = 620;
const CANVAS_H = 420;

// ─── Helpers canvas ──────────────────────────────────────────────────────────
function drawFloorplan(ctx, floorplanUrl, imgRef, imgLoaded, imgTransform, imgRect) {
  if (floorplanUrl && imgRef.current && imgLoaded) {
    const tr = imgTransform || { rotation: 0 };
    const ir = imgRect || { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H };
    ctx.save();
    ctx.translate(ir.x + ir.w / 2, ir.y + ir.h / 2);
    ctx.rotate(tr.rotation * Math.PI / 180);
    ctx.drawImage(imgRef.current, -ir.w / 2, -ir.h / 2, ir.w, ir.h);
    ctx.restore();
  } else {
    ctx.fillStyle = '#F0F4F8'; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.strokeStyle = '#DDE3EA'; ctx.lineWidth = 1;
    for (let x = 0; x <= CANVAS_W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, CANVAS_H); ctx.stroke(); }
    for (let y = 0; y <= CANVAS_H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(CANVAS_W, y); ctx.stroke(); }
  }
}

function useFloorplanImg(floorplanUrl, onLoad) {
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  useEffect(() => {
    if (!floorplanUrl) { setImgLoaded(false); imgRef.current = null; return; }
    const img = new Image();
    img.onload = () => { imgRef.current = img; setImgLoaded(true); if (onLoad) onLoad(); };
    img.onerror = () => setImgLoaded(false);
    img.src = floorplanUrl;
  }, [floorplanUrl]); // eslint-disable-line
  return { imgRef, imgLoaded };
}

// ─── Tab 0 : PlanCanvas (espace détection + entrées) ─────────────────────────
function PlanCanvas({ mode, detectionRect, entrances, onDetectionRect, onEntranceToggle,
                      zones, sensors, floorplanUrl, imgTransform, imgRect, dimensions }) {
  const canvasRef = useRef(null);
  const [drawing,     setDrawing]     = useState(false);
  const [startPos,    setStartPos]    = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const { imgRef, imgLoaded } = useFloorplanImg(floorplanUrl);

  const siteW = dimensions.width_cm  || 1000;
  const siteH = dimensions.height_cm || 800;
  const cmToPx = useCallback((cx, cy) => ({
    x: (cx / siteW) * CANVAS_W,
    y: CANVAS_H - (cy / siteH) * CANVAS_H
  }), [siteW, siteH]);
  const pxToCm = useCallback((px, py) => ({
    x: Math.round(Math.max(0, Math.min(siteW, (px / CANVAS_W) * siteW))),
    y: Math.round(Math.max(0, Math.min(siteH, ((CANVAS_H - py) / CANVAS_H) * siteH)))
  }), [siteW, siteH]);
  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (CANVAS_W / r.width), y: (e.clientY - r.top) * (CANVAS_H / r.height) };
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    drawFloorplan(ctx, floorplanUrl, imgRef, imgLoaded, imgTransform, imgRect);

    // Zones légères en fond
    (zones || []).forEach(z => {
      const tl = cmToPx(z.x_min_cm, z.y_max_cm), br = cmToPx(z.x_max_cm, z.y_min_cm);
      ctx.fillStyle = z.color + '18'; ctx.fillRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
    });

    // Espace de détection
    if (detectionRect) {
      const tl = cmToPx(detectionRect.x_min_cm, detectionRect.y_max_cm);
      const br = cmToPx(detectionRect.x_max_cm, detectionRect.y_min_cm);
      const w = br.x - tl.x, h = br.y - tl.y;
      ctx.fillStyle = 'rgba(25,118,210,0.09)'; ctx.fillRect(tl.x, tl.y, w, h);
      ctx.strokeStyle = '#1565C0'; ctx.lineWidth = 2.5; ctx.setLineDash([10, 5]);
      ctx.strokeRect(tl.x, tl.y, w, h); ctx.setLineDash([]);
      ctx.fillStyle = '#1565C0'; ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
      ctx.fillText('Espace de détection', tl.x + 6, tl.y + 6);
    }

    // Rectangle en cours de dessin
    if (currentRect && mode === 'detection') {
      ctx.fillStyle = 'rgba(25,118,210,0.12)'; ctx.fillRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h);
      ctx.strokeStyle = '#1565C0'; ctx.lineWidth = 2; ctx.setLineDash([6, 3]);
      ctx.strokeRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h); ctx.setLineDash([]);
    }

    // Capteurs (petits points discrets)
    (sensors || []).filter(s => s.x_cm > 0 || s.y_cm > 0).forEach(s => {
      const p = cmToPx(s.x_cm, s.y_cm);
      ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = s.is_online ? '#1F4E79AA' : '#9E9E9EAA'; ctx.fill();
    });

    // Entrées
    (entrances || []).forEach((ent, i) => {
      const p = cmToPx(ent.x_cm, ent.y_cm);
      ctx.beginPath(); ctx.arc(p.x, p.y, 13, 0, Math.PI * 2);
      ctx.fillStyle = '#2E7D32'; ctx.fill();
      ctx.strokeStyle = '#FFF'; ctx.lineWidth = 2; ctx.stroke();
      ctx.fillStyle = '#FFF'; ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('E' + (i + 1), p.x, p.y);
      if (ent.label) {
        ctx.font = 'bold 9px sans-serif'; ctx.fillStyle = '#1B5E20';
        ctx.textAlign = 'left'; ctx.textBaseline = 'top';
        ctx.fillText(ent.label, p.x + 15, p.y - 7);
      }
    });

    // Hint mode
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.font = 'bold 11px sans-serif';
    if (mode === 'detection' && !detectionRect) {
      ctx.fillStyle = 'rgba(21,101,192,0.8)';
      ctx.fillText('Glissez pour définir l\'espace de détection', CANVAS_W / 2, CANVAS_H - 4);
    } else if (mode === 'entrance') {
      ctx.fillStyle = 'rgba(46,125,50,0.8)';
      ctx.fillText('Clic = ajouter une entrée · Clic sur E = supprimer', CANVAS_W / 2, CANVAS_H - 4);
    }
  }, [detectionRect, entrances, zones, sensors, cmToPx, currentRect, mode, floorplanUrl, imgLoaded, imgTransform, imgRect, imgRef]);

  useEffect(() => { draw(); }, [draw]);

  const onMouseDown = (e) => { if (mode !== 'detection') return; setDrawing(true); setStartPos(getPos(e)); };
  const onMouseMove = (e) => {
    if (!drawing || !startPos || mode !== 'detection') return;
    const p = getPos(e);
    setCurrentRect({ x: Math.min(startPos.x, p.x), y: Math.min(startPos.y, p.y),
      w: Math.abs(p.x - startPos.x), h: Math.abs(p.y - startPos.y) });
  };
  const onMouseUp = (e) => {
    if (mode === 'detection') {
      if (drawing && startPos) {
        const p = getPos(e);
        if (Math.abs(p.x - startPos.x) > 15 && Math.abs(p.y - startPos.y) > 15) {
          const minX = Math.min(startPos.x, p.x), maxX = Math.max(startPos.x, p.x);
          const minY = Math.min(startPos.y, p.y), maxY = Math.max(startPos.y, p.y);
          const tl = pxToCm(minX, minY), br = pxToCm(maxX, maxY);
          onDetectionRect({ x_min_cm: Math.min(tl.x, br.x), x_max_cm: Math.max(tl.x, br.x), y_min_cm: Math.min(tl.y, br.y), y_max_cm: Math.max(tl.y, br.y) });
        }
        setDrawing(false); setStartPos(null); setCurrentRect(null);
      }
    } else if (mode === 'entrance') {
      const p = getPos(e);
      const idx = (entrances || []).findIndex(ent => { const ep = cmToPx(ent.x_cm, ent.y_cm); return Math.hypot(p.x - ep.x, p.y - ep.y) < 16; });
      const cm = pxToCm(p.x, p.y);
      onEntranceToggle(idx >= 0 ? { remove: idx } : { add: { x_cm: cm.x, y_cm: cm.y, label: '' } });
    }
  };

  return (
    <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
      style={{ width: '100%', maxWidth: CANVAS_W, border: '1px solid #DDD', borderRadius: 8,
        cursor: mode === 'detection' ? 'crosshair' : 'cell', display: 'block' }} />
  );
}

// ─── Tab 2 : ZoneCanvas (zones analytiques) ──────────────────────────────────
function ZoneCanvas({ zones, dimensions, onZoneDrawn, selectedColor, floorplanUrl, sensors, imgTransform, imgRect, detectionRect }) {
  const canvasRef = useRef(null);
  const [drawing,     setDrawing]     = useState(false);
  const [startPos,    setStartPos]    = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const { imgRef, imgLoaded } = useFloorplanImg(floorplanUrl);

  const siteW = dimensions.width_cm  || 1000;
  const siteH = dimensions.height_cm || 800;
  const pxToCm = useCallback((px, py) => ({ x: Math.round((px / CANVAS_W) * siteW), y: Math.round(((CANVAS_H - py) / CANVAS_H) * siteH) }), [siteW, siteH]);
  const cmToPx = useCallback((cx, cy) => ({ x: (cx / siteW) * CANVAS_W, y: CANVAS_H - (cy / siteH) * CANVAS_H }), [siteW, siteH]);
  const getPos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return { x: (e.clientX - r.left) * (CANVAS_W / r.width), y: (e.clientY - r.top) * (CANVAS_H / r.height) }; };

  const draw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    drawFloorplan(ctx, floorplanUrl, imgRef, imgLoaded, imgTransform, imgRect);

    // Espace de détection en fond
    if (detectionRect) {
      const tl = cmToPx(detectionRect.x_min_cm, detectionRect.y_max_cm);
      const br = cmToPx(detectionRect.x_max_cm, detectionRect.y_min_cm);
      ctx.fillStyle = 'rgba(25,118,210,0.06)'; ctx.fillRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
      ctx.strokeStyle = '#1565C055'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 3]);
      ctx.strokeRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y); ctx.setLineDash([]);
    }

    zones.forEach(zone => {
      const tl = cmToPx(zone.x_min_cm, zone.y_max_cm), br = cmToPx(zone.x_max_cm, zone.y_min_cm);
      const w = br.x - tl.x, h = br.y - tl.y;
      ctx.fillStyle = zone.color + '33'; ctx.fillRect(tl.x, tl.y, w, h);
      ctx.strokeStyle = zone.color; ctx.lineWidth = 2; ctx.strokeRect(tl.x, tl.y, w, h);
      ctx.fillStyle = zone.color; ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
      ctx.fillText(zone.name, tl.x + 5, tl.y + 5);
    });

    (sensors || []).filter(s => s.x_cm || s.y_cm).forEach(s => {
      const p = cmToPx(s.x_cm, s.y_cm);
      ctx.beginPath(); ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = s.is_online ? '#1F4E79' : '#9E9E9E'; ctx.fill();
      ctx.strokeStyle = '#FFF'; ctx.lineWidth = 1.5; ctx.stroke();
    });

    if (currentRect) {
      ctx.fillStyle = selectedColor + '33'; ctx.fillRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h);
      ctx.strokeStyle = selectedColor; ctx.lineWidth = 2; ctx.setLineDash([5, 3]);
      ctx.strokeRect(currentRect.x, currentRect.y, currentRect.w, currentRect.h); ctx.setLineDash([]);
    }
  }, [zones, sensors, cmToPx, currentRect, selectedColor, floorplanUrl, imgLoaded, imgTransform, imgRect, detectionRect, imgRef]);

  useEffect(() => { draw(); }, [draw]);

  const onMouseDown = (e) => { setDrawing(true); setStartPos(getPos(e)); };
  const onMouseMove = (e) => {
    if (!drawing || !startPos) return;
    const p = getPos(e);
    setCurrentRect({ x: Math.min(startPos.x, p.x), y: Math.min(startPos.y, p.y), w: Math.abs(p.x - startPos.x), h: Math.abs(p.y - startPos.y) });
  };
  const onMouseUp = (e) => {
    if (!drawing || !startPos) return;
    setDrawing(false);
    const p = getPos(e);
    const minX = Math.min(startPos.x, p.x), maxX = Math.max(startPos.x, p.x);
    const minY = Math.min(startPos.y, p.y), maxY = Math.max(startPos.y, p.y);
    if (Math.abs(maxX - minX) > 10 && Math.abs(maxY - minY) > 10) {
      const tl = pxToCm(minX, minY), br = pxToCm(maxX, maxY);
      onZoneDrawn({ x_min_cm: Math.min(tl.x, br.x), x_max_cm: Math.max(tl.x, br.x), y_min_cm: Math.min(tl.y, br.y), y_max_cm: Math.max(tl.y, br.y) });
    }
    setStartPos(null); setCurrentRect(null);
  };

  return (
    <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
      style={{ width: '100%', maxWidth: CANVAS_W, border: '1px solid #DDD', borderRadius: 8, cursor: 'crosshair', display: 'block' }} />
  );
}

// ─── Tab 1 : SensorCanvas (placement + couverture) ───────────────────────────
function SensorCanvas({ sensors, dimensions, floorplanUrl, onPositionChange, draggedSensor, zones,
                        imgTransform, imgRect, calibrations, showCoverage, onCoverageScore,
                        suggestedPositions, detectionRect, entrances, entranceSensorIds }) {
  const canvasRef  = useRef(null);
  const [dragging,  setDragging]  = useState(null);
  const [positions, setPositions] = useState({});
  const { imgRef, imgLoaded } = useFloorplanImg(floorplanUrl);

  const siteW = dimensions.width_cm  || 1000;
  const siteH = dimensions.height_cm || 800;
  const cmToPx = useCallback((cx, cy) => ({ x: (cx / siteW) * CANVAS_W, y: CANVAS_H - (cy / siteH) * CANVAS_H }), [siteW, siteH]);
  const pxToCm = useCallback((px, py) => ({
    x_cm: Math.round(Math.max(0, Math.min(siteW, (px / CANVAS_W) * siteW))),
    y_cm: Math.round(Math.max(0, Math.min(siteH, ((CANVAS_H - py) / CANVAS_H) * siteH)))
  }), [siteW, siteH]);
  const getPos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return { x: (e.clientX - r.left) * (CANVAS_W / r.width), y: (e.clientY - r.top) * (CANVAS_H / r.height) }; };

  useEffect(() => {
    const init = {};
    sensors.forEach(s => { init[s.sensor_id] = { x_cm: s.x_cm || 0, y_cm: s.y_cm || 0 }; });
    setPositions(init);
  }, [sensors]);

  const placed = sensors.filter(s => { const p = positions[s.sensor_id]; return p && (p.x_cm > 0 || p.y_cm > 0); });

  const computeCoverage = useCallback(() => {
    if (!placed.length) return null;
    const GRID = 15, COLS = Math.ceil(CANVAS_W / GRID), ROWS = Math.ceil(CANVAS_H / GRID), TOTAL = COLS * ROWS;
    const grid = new Int8Array(TOTAL);

    // Detection rect in px for filtering
    let drTl = null, drBr = null;
    if (detectionRect) {
      drTl = cmToPx(detectionRect.x_min_cm, detectionRect.y_max_cm);
      drBr = cmToPx(detectionRect.x_max_cm, detectionRect.y_min_cm);
    }

    placed.forEach(s => {
      const pos = positions[s.sensor_id]; if (!pos) return;
      const cal = calibrations[s.sensor_id] || { txpower: -59, n_factor: 2.5 };
      const rM  = Math.min(25, Math.pow(10, (cal.txpower - (-65)) / (10 * Math.max(0.5, cal.n_factor))));
      const rPx = (rM * 100 / siteW) * CANVAS_W;
      const sp  = cmToPx(pos.x_cm, pos.y_cm);
      for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
        const cx = c * GRID + 7.5, cy = r * GRID + 7.5;
        if (drTl && (cx < drTl.x || cx > drBr.x || cy < drTl.y || cy > drBr.y)) continue;
        if (Math.hypot(cx - sp.x, cy - sp.y) <= rPx) grid[r * COLS + c]++;
      }
    });

    // Score sur l'espace de détection seulement (ou tout le canvas)
    let total = 0, covered2 = 0;
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      const cx = c * GRID + 7.5, cy = r * GRID + 7.5;
      if (drTl && (cx < drTl.x || cx > drBr.x || cy < drTl.y || cy > drBr.y)) continue;
      total++;
      if (grid[r * COLS + c] >= 2) covered2++;
    }
    const score = total > 0 ? Math.round((covered2 / total) * 100) : 0;
    return { grid, COLS, ROWS, GRID, score };
  }, [placed, positions, calibrations, cmToPx, siteW, detectionRect]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    drawFloorplan(ctx, floorplanUrl, imgRef, imgLoaded, imgTransform, imgRect);

    // Coverage overlay
    if (showCoverage && placed.length > 0) {
      const cov = computeCoverage();
      if (cov) {
        ctx.save(); ctx.globalAlpha = 0.38;
        for (let r = 0; r < cov.ROWS; r++) for (let c = 0; c < cov.COLS; c++) {
          const n = cov.grid[r * cov.COLS + c];
          if (n === 0) ctx.fillStyle = '#B0B0B0';
          else if (n === 1) ctx.fillStyle = '#FF5722';
          else if (n === 2) ctx.fillStyle = '#8BC34A';
          else ctx.fillStyle = '#1B5E20';
          ctx.fillRect(c * cov.GRID, r * cov.GRID, cov.GRID, cov.GRID);
        }
        ctx.restore();
      }
    }

    // Detection rect border
    if (detectionRect) {
      const tl = cmToPx(detectionRect.x_min_cm, detectionRect.y_max_cm);
      const br = cmToPx(detectionRect.x_max_cm, detectionRect.y_min_cm);
      ctx.strokeStyle = '#1565C0'; ctx.lineWidth = 2; ctx.setLineDash([8, 4]);
      ctx.strokeRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y); ctx.setLineDash([]);
    }

    // Zones
    (zones || []).forEach(zone => {
      const tl = cmToPx(zone.x_min_cm, zone.y_max_cm), br = cmToPx(zone.x_max_cm, zone.y_min_cm);
      ctx.fillStyle = zone.color + '1A'; ctx.fillRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
      ctx.strokeStyle = zone.color + '88'; ctx.lineWidth = 1.5; ctx.setLineDash([4, 3]);
      ctx.strokeRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y); ctx.setLineDash([]);
      ctx.fillStyle = zone.color + 'BB'; ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
      ctx.fillText(zone.name, tl.x + 4, tl.y + 4);
    });

    // Entrance markers (before sensors so sensors draw on top)
    (entrances || []).forEach((ent, i) => {
      const p = cmToPx(ent.x_cm, ent.y_cm);
      ctx.save(); ctx.globalAlpha = 0.4;
      ctx.beginPath(); ctx.arc(p.x, p.y, 20, 0, Math.PI * 2);
      ctx.fillStyle = '#2E7D32'; ctx.fill(); ctx.restore();
      ctx.fillStyle = '#2E7D32'; ctx.font = 'bold 8px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText('E' + (i + 1), p.x, p.y + 22);
    });

    // Sensors + coverage circles
    placed.forEach(s => {
      const pos = positions[s.sensor_id]; if (!pos) return;
      const p   = cmToPx(pos.x_cm, pos.y_cm);
      const isDragged  = dragging === s.sensor_id;
      const isEntrance = (entranceSensorIds || []).includes(s.sensor_id);
      const cal = calibrations[s.sensor_id] || { txpower: -59, n_factor: 2.5 };

      if (showCoverage) {
        const r75m  = Math.min(25, Math.pow(10, (cal.txpower - (-75)) / (10 * Math.max(0.5, cal.n_factor))));
        const r75px = (r75m * 100 / siteW) * CANVAS_W;
        ctx.beginPath(); ctx.arc(p.x, p.y, r75px, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(100,100,255,0.25)'; ctx.lineWidth = 1; ctx.setLineDash([6, 3]); ctx.stroke(); ctx.setLineDash([]);
        const r65m  = Math.min(25, Math.pow(10, (cal.txpower - (-65)) / (10 * Math.max(0.5, cal.n_factor))));
        const r65px = Math.max(8, (r65m * 100 / siteW) * CANVAS_W);
        const badCalib = (r65m * 100 / siteW) * CANVAS_W < 8;
        ctx.beginPath(); ctx.arc(p.x, p.y, r65px, 0, Math.PI * 2);
        ctx.strokeStyle = badCalib ? 'rgba(255,80,80,0.7)' : 'rgba(100,100,255,0.55)';
        ctx.lineWidth = badCalib ? 2 : 1.5;
        if (badCalib) ctx.setLineDash([3, 3]); ctx.stroke(); ctx.setLineDash([]);
      }

      if (isDragged) { ctx.shadowBlur = 14; ctx.shadowColor = 'rgba(0,0,0,0.4)'; }
      ctx.beginPath(); ctx.arc(p.x, p.y, 16, 0, Math.PI * 2);
      ctx.fillStyle = isEntrance ? '#2E7D32' : (s.is_online ? '#1F4E79' : '#9E9E9E'); ctx.fill();
      ctx.strokeStyle = isDragged ? '#FF9800' : '#FFF'; ctx.lineWidth = isDragged ? 3 : 2; ctx.stroke();
      ctx.shadowBlur = 0;

      // Entrance badge
      if (isEntrance) {
        ctx.beginPath(); ctx.arc(p.x + 11, p.y - 11, 7, 0, Math.PI * 2);
        ctx.fillStyle = '#FF9800'; ctx.fill();
        ctx.strokeStyle = '#FFF'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.fillStyle = '#FFF'; ctx.font = 'bold 7px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('E', p.x + 11, p.y - 11);
      }

      ctx.fillStyle = '#FFF'; ctx.font = 'bold 7px sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('WiFi', p.x, p.y);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';

      const _raw = s.sensor_id.startsWith('REDIA-') ? s.sensor_id.slice(6).replace('-', '/') : s.sensor_id;
      const label = s.display_name || _raw;
      const sublabel = s.display_name ? _raw : null;
      const boxH = sublabel ? 28 : 17;
      ctx.font = 'bold 11px sans-serif';
      const tw = Math.max(ctx.measureText(label).width, sublabel ? ctx.measureText(sublabel).width : 0);
      ctx.fillStyle = 'rgba(20,20,20,0.78)'; ctx.fillRect(p.x + 19, p.y - 12, tw + 6, boxH);
      ctx.fillStyle = '#FFF'; ctx.fillText(label, p.x + 22, p.y + 2);
      if (sublabel) { ctx.font = '9px sans-serif'; ctx.fillStyle = '#AAD4FF'; ctx.fillText(sublabel, p.x + 22, p.y + 14); }
      ctx.fillStyle = '#888'; ctx.font = '9px sans-serif';
      ctx.fillText(pos.x_cm + 'cm, ' + pos.y_cm + 'cm', p.x + 19, p.y + 16);
      const sData = sensors.find(sx => sx.sensor_id === s.sensor_id);
      if (sData && sData.zone_name) { ctx.font = 'bold 10px sans-serif'; ctx.fillStyle = '#E91E63'; ctx.fillText('[ ' + sData.zone_name + ' ]', p.x + 19, p.y + 28); }
    });

    // Suggested positions
    if (suggestedPositions) {
      Object.entries(suggestedPositions).forEach(([sid, pos]) => {
        const p = cmToPx(pos.x_cm, pos.y_cm);
        ctx.save(); ctx.globalAlpha = 0.6;
        ctx.beginPath(); ctx.arc(p.x, p.y, 18, 0, Math.PI * 2);
        ctx.fillStyle = '#FF9800'; ctx.fill();
        ctx.strokeStyle = '#FFF'; ctx.lineWidth = 2.5; ctx.stroke();
        ctx.fillStyle = '#FFF'; ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('OPT', p.x, p.y); ctx.restore();
        const cur = positions[sid];
        if (cur && (cur.x_cm || cur.y_cm)) {
          const cp = cmToPx(cur.x_cm, cur.y_cm);
          if (Math.hypot(p.x - cp.x, p.y - cp.y) > 8) {
            ctx.save(); ctx.globalAlpha = 0.4; ctx.strokeStyle = '#FF9800'; ctx.lineWidth = 1.5;
            ctx.setLineDash([4, 3]); ctx.beginPath(); ctx.moveTo(cp.x, cp.y); ctx.lineTo(p.x, p.y); ctx.stroke();
            ctx.setLineDash([]); ctx.restore();
          }
        }
      });
    }

    // Drop hint
    if (draggedSensor) {
      ctx.strokeStyle = '#FF9800'; ctx.lineWidth = 2; ctx.setLineDash([8, 4]);
      ctx.strokeRect(4, 4, CANVAS_W - 8, CANVAS_H - 8); ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(255,152,0,0.06)'; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#FF9800'; ctx.font = 'bold 14px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('Déposer ici pour positionner', CANVAS_W / 2, CANVAS_H / 2); ctx.textAlign = 'left';
    }
  }, [placed, positions, cmToPx, dragging, floorplanUrl, imgLoaded, draggedSensor, imgTransform, imgRect,
      zones, showCoverage, calibrations, computeCoverage, siteW, sensors, suggestedPositions,
      detectionRect, entrances, entranceSensorIds, imgRef]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    if (!onCoverageScore) return;
    if (!showCoverage) { onCoverageScore(null); return; }
    const cov = computeCoverage();
    onCoverageScore(cov ? cov.score : null);
  }, [showCoverage, computeCoverage, onCoverageScore]);

  const hitTest = (px, py) => { for (const s of placed) { const p = cmToPx(positions[s.sensor_id].x_cm, positions[s.sensor_id].y_cm); if (Math.hypot(px - p.x, py - p.y) < 20) return s.sensor_id; } return null; };
  const onMouseDown = (e) => { const p = getPos(e); const hit = hitTest(p.x, p.y); if (hit) setDragging(hit); };
  const onMouseMove = (e) => { if (!dragging) return; const p = getPos(e); setPositions(prev => ({ ...prev, [dragging]: pxToCm(p.x, p.y) })); };
  const onMouseUp   = () => { if (!dragging) return; const pos = positions[dragging]; if (pos) onPositionChange(dragging, pos); setDragging(null); };
  // Clic droit sur un capteur pose : le remet dans la liste "a placer".
  // Le bouton equivalent existe dans la liste laterale, mais il est plus
  // naturel de retirer un capteur la ou on l'a depose.
  const onContextMenu = (e) => {
    e.preventDefault();
    const p = getPos(e);
    const hit = hitTest(p.x, p.y);
    if (!hit) return;
    const s = (sensors || []).find(x => x.sensor_id === hit);
    const nom = (s && s.display_name) ? s.display_name + ' (' + hit + ')' : hit;
    if (!window.confirm('Retirer ' + nom + ' du plan ?')) return;
    setDragging(null);
    onPositionChange(hit, { x_cm: 0, y_cm: 0 });
  };
  const onDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
  const onDrop = (e) => {
    e.preventDefault();
    const sensorId = e.dataTransfer.getData('sensor_id'); if (!sensorId) return;
    const p = getPos(e); const pos = pxToCm(p.x, p.y);
    setPositions(prev => ({ ...prev, [sensorId]: pos }));
    onPositionChange(sensorId, pos);
  };

  return (
    <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
      onContextMenu={onContextMenu}
      onDragOver={onDragOver} onDrop={onDrop}
      style={{ width: '100%', maxWidth: CANVAS_W, border: draggedSensor ? '2px dashed #FF9800' : '1px solid #DDD',
        borderRadius: 8, cursor: dragging ? 'grabbing' : 'default', display: 'block' }} />
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
// Tab 3 : carte de frequentation
//
// La carte est calculee ET DESSINEE par le serveur (zonage gradue, valide a
// 2,1 points d ecart par zone hors echantillon). On ne la redessine pas ici :
// deux implementations de la meme carte finiraient par diverger.
//
// Le SVG passe par axios et non par une balise <img src=...> : l endpoint
// exige le jeton, qu une balise <img> ne sait pas envoyer. On en fait une URL
// de blob, ce qui evite au passage d injecter du balisage dans le DOM.
function CarteFrequentation({ siteId }) {
  const [url,    setUrl]    = useState(null);
  const [erreur, setErreur] = useState(null);
  const [charge, setCharge] = useState(false);
  const jour = (d) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);
  const [debut, setDebut] = useState(jour(7));
  const [fin,   setFin]   = useState(jour(1));

  const charger = useCallback(async () => {
    if (!siteId) return;
    setCharge(true); setErreur(null);
    try {
      const r = await zonesAPI.zonage(siteId, debut, fin);
      const blob = new Blob([r.data], { type: 'image/svg+xml' });
      setUrl(u => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(blob); });
    } catch (e) {
      setErreur(e.response?.data?.detail || e.message);
      setUrl(u => { if (u) URL.revokeObjectURL(u); return null; });
    } finally { setCharge(false); }
  }, [siteId, debut, fin]);

  useEffect(() => { charger(); }, [charger]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField label="Du" type="date" size="small" value={debut}
                   onChange={e => setDebut(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Au" type="date" size="small" value={fin}
                   onChange={e => setFin(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="outlined" size="small" onClick={charger} disabled={charge}>
          {charge ? 'Calcul en cours' : 'Actualiser'}
        </Button>
        {url && (
          <Button size="small" href={url} download={'carte-' + siteId + '-' + debut + '.svg'}>
            Telecharger
          </Button>
        )}
      </Box>
      {erreur && <Alert severity="warning" sx={{ mb: 2 }}>{String(erreur)}</Alert>}
      {url && (
        <Box component="img" src={url} alt="Carte de frequentation"
             sx={{ width: '100%', maxWidth: 900, display: 'block' }} />
      )}
      {!url && !erreur && !charge && (
        <Typography variant="caption" color="text.secondary">
          Aucune carte pour cette periode.
        </Typography>
      )}
    </Box>
  );
}

export default function Zones() {
  const [tab,          setTab]          = useState(0);
  const [sites,        setSites]        = useState([]);
  const [siteId,       setSiteId]       = useState('');
  const [zones,        setZones]        = useState([]);
  const [sensors,      setSensors]      = useState([]);
  const [calibrations, setCalibrations] = useState({});
  const [dimensions,   setDimensions]   = useState({ width_cm: 0, height_cm: 0 });
  const [floorplanUrl, setFloorplanUrl] = useState(null);
  const [hasFloorplan, setHasFloorplan] = useState(false);
  const [selectedColor,setSelectedColor]= useState('#3498DB');
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [dimForm,      setDimForm]      = useState({ width_cm: '', height_cm: '' });
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [dimDialog,    setDimDialog]    = useState(false);
  const [editingZone,  setEditingZone]  = useState(null);
  const [drawnCoords,  setDrawnCoords]  = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');
  const [draggedSensor,setDraggedSensor]= useState(null);
  const [imgTransform,  setImgTransform]  = useState({ rotation: 0, scale: 1 });
  const [imgRect,       setImgRect]       = useState({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H });
  const [showCoverage,  setShowCoverage]  = useState(true);
  const [coverageScore, setCoverageScore] = useState(null);
  const [layoutSaved,   setLayoutSaved]   = useState(false);

  // Step 1 — Plan
  const [planMode,         setPlanMode]         = useState('detection');
  const [detectionRect,    setDetectionRect]    = useState(null);
  const [entrances,        setEntrances]        = useState([]);
  const [entranceLabelDlg, setEntranceLabelDlg] = useState(null); // { idx, label }

  // Step 2 — Capteurs
  const [entranceSensorIds, setEntranceSensorIds] = useState([]);
  const [suggestedPositions,setSuggestedPositions]= useState(null);
  const [optimizing,        setOptimizing]        = useState(false);
  const [optimizedScore,    setOptimizedScore]    = useState(null);
  const [optimizedAniso,    setOptimizedAniso]    = useState(null);

  const saveFloorplanLayout = (sid, transform, rect) => {
    api.put('/v1/sites/' + sid + '/dimensions', { width_cm: dimensions.width_cm, height_cm: dimensions.height_cm, floorplan_layout: { transform, rect } }).catch(() => {});
  };

  const savePlanData = useCallback((sid, dr, ents, esids) => {
    api.put('/v1/sites/' + sid + '/dimensions', {
      width_cm: dimensions.width_cm, height_cm: dimensions.height_cm,
      detection_rect: dr, entrances: ents, entrance_sensor_ids: esids
    }).catch(() => {});
  }, [dimensions]);

  useEffect(() => { sitesAPI.list().then(r => setSites(r.data.sites || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!siteId) return;
    setFloorplanUrl(null); setZones([]); setSensors([]); setHasFloorplan(false);
    setCalibrations({}); setSuggestedPositions(null); setOptimizedScore(null);
    setDetectionRect(null); setEntrances([]); setEntranceSensorIds([]);

    zonesAPI.list(siteId).then(r => setZones((r.data && r.data.zones) || r.data || [])).catch(() => {});

    api.get('/v1/sites/' + siteId + '/dimensions').then(r => {
      setDimensions(r.data);
      setDimForm({ width_cm: r.data.width_cm || '', height_cm: r.data.height_cm || '' });
      const fl = r.data.floorplan_layout;
      if (fl) { setImgTransform(fl.transform || { rotation: 0, scale: 1 }); setImgRect(fl.rect || { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H }); }
      else { setImgTransform({ rotation: 0, scale: 1 }); setImgRect({ x: 0, y: 0, w: CANVAS_W, h: CANVAS_H }); }
      if (r.data.detection_rect) setDetectionRect(r.data.detection_rect);
      if (r.data.entrances)      setEntrances(r.data.entrances);
      if (r.data.entrance_sensor_ids) setEntranceSensorIds(r.data.entrance_sensor_ids);
    }).catch(() => {});

    api.get('/v1/sites/' + siteId).then(r => {
      if (r.data && r.data.site && r.data.site.floorplan_path) {
        setFloorplanUrl(floorplanAPI.getUrl(siteId) + '?t=' + Date.now()); setHasFloorplan(true);
      }
    }).catch(() => {});

    // Capteurs DEPLOYES sur ce site — y compris ceux partis ailleurs depuis.
    // Leur geometrie vient de sensor_deployments, pas de sensors.
    api.get('/v1/sites/' + siteId + '/deployed-sensors').then(r => {
      const dep = (r.data && r.data.sensors) || [];
      setSensors(dep.map(d => ({
        sensor_id:   d.sensor_id,
        // Le champ libelle reflete la zone du deploiement, pas le nom courant du
        // capteur : sur un site termine, il porte le nom qu'il avait la-bas.
        display_name: d.libelle || d.display_name,
        role:        d.role,
        is_online:   d.is_online,
        x_cm:        d.x_cm || 0,
        y_cm:        d.y_cm || 0,
        zone_name:   d.zone_name,
        ailleurs:    d.ailleurs,      // site actuel si le capteur a bouge
        actif:       d.actif,
      })));
    }).catch(() => {
      // Repli sur l'ancien mode si l'endpoint n'est pas disponible
      api.get('/v1/tracking/status').then(r => {
        const all = (r.data && r.data.sensors) || [];
        setSensors(all.filter(s => s.site_id === siteId));
      }).catch(() => {});
    });

    api.get('/v1/sites/' + siteId + '/calibrations').then(r => {
      const map = {}; (r.data.calibrations || []).forEach(c => { map[c.sensor_id] = c; }); setCalibrations(map);
    }).catch(() => {});
  }, [siteId]);

  // Handlers Plan tab
  const handleDetectionRect = (rect) => {
    setDetectionRect(rect);
    savePlanData(siteId, rect, entrances, entranceSensorIds);
    setSuccess('Espace de détection sauvegardé');
  };
  const handleEntranceToggle = ({ add, remove }) => {
    if (add !== undefined) {
      const newEnts = [...entrances, add];
      setEntrances(newEnts);
      // Ask for label
      setEntranceLabelDlg({ idx: newEnts.length - 1, label: '' });
      savePlanData(siteId, detectionRect, newEnts, entranceSensorIds);
    } else if (remove !== undefined) {
      const newEnts = entrances.filter((_, i) => i !== remove);
      setEntrances(newEnts);
      savePlanData(siteId, detectionRect, newEnts, entranceSensorIds);
    }
  };
  const saveEntranceLabel = () => {
    if (!entranceLabelDlg) return;
    const newEnts = entrances.map((e, i) => i === entranceLabelDlg.idx ? { ...e, label: entranceLabelDlg.label } : e);
    setEntrances(newEnts);
    savePlanData(siteId, detectionRect, newEnts, entranceSensorIds);
    setEntranceLabelDlg(null);
    setSuccess('Entrée ajoutée');
  };

  // Toggle entrance sensor
  const toggleEntranceSensor = (sensorId) => {
    const newIds = entranceSensorIds.includes(sensorId)
      ? entranceSensorIds.filter(id => id !== sensorId)
      : [...entranceSensorIds, sensorId];
    setEntranceSensorIds(newIds);
    savePlanData(siteId, detectionRect, entrances, newIds);
  };

  // Optimization
  const runOptimization = useCallback(() => {
    const placed_snap = sensors.filter(s => s.x_cm > 0 || s.y_cm > 0);
    const free_snap   = placed_snap.filter(s => !entranceSensorIds.includes(s.sensor_id));
    const fixed_snap  = placed_snap.filter(s =>  entranceSensorIds.includes(s.sensor_id));
    if (placed_snap.length < 2) return;
    setOptimizing(true); setSuggestedPositions(null); setOptimizedScore(null); setOptimizedAniso(null);
    const cal_snap = calibrations;
    const sW = dimensions.width_cm || 1000, sH = dimensions.height_cm || 800;
    const dr = detectionRect;
    const GPOW = 15, CW = 620, CH = 420, COLS = Math.ceil(CW / GPOW), ROWS = Math.ceil(CH / GPOW);
    let drTlX = 0, drTlY = 0, drBrX = CW, drBrY = CH;
    if (dr) {
      drTlX = (dr.x_min_cm / sW) * CW; drBrX = (dr.x_max_cm / sW) * CW;
      drTlY = CH - (dr.y_max_cm / sH) * CH; drBrY = CH - (dr.y_min_cm / sH) * CH;
    }
    const score = (posMap) => {
      const grid = new Int8Array(ROWS * COLS);
      placed_snap.forEach(s => {
        const pos = posMap[s.sensor_id]; if (!pos) return;
        const c = cal_snap[s.sensor_id] || { txpower: -59, n_factor: 2.5 };
        const rM  = Math.min(25, Math.pow(10, (c.txpower - (-65)) / (10 * Math.max(0.5, c.n_factor))));
        const rPx = (rM * 100 / sW) * CW;
        const sx  = (pos.x_cm / sW) * CW, sy = CH - (pos.y_cm / sH) * CH;
        for (let r = 0; r < ROWS; r++) for (let c2 = 0; c2 < COLS; c2++) {
          const cx = c2 * GPOW + 7.5, cy = r * GPOW + 7.5;
          if (cx < drTlX || cx > drBrX || cy < drTlY || cy > drBrY) continue;
          if (Math.hypot(cx - sx, cy - sy) <= rPx) grid[r * COLS + c2]++;
        }
      });
      let tot = 0, n2 = 0;
      for (let r = 0; r < ROWS; r++) for (let c2 = 0; c2 < COLS; c2++) {
        const cx = c2 * GPOW + 7.5, cy = r * GPOW + 7.5;
        if (cx < drTlX || cx > drBrX || cy < drTlY || cy > drBrY) continue;
        tot++; if (grid[r * COLS + c2] >= 2) n2++;
      }
      const cov = tot > 0 ? (n2 / tot) * 100 : 0;
      // On pondere par la geometrie : une couverture parfaite obtenue avec des
      // capteurs alignes ne vaut pas une couverture correcte bien repartie.
      const an = anisotropyOf(Object.values(posMap));
      return cov * geoPenalty(an);
    };
    // Couverture brute, sans ponderation — pour l'affichage
    const rawCoverage = (posMap) => {
      const grid = new Int8Array(ROWS * COLS);
      placed_snap.forEach(s => {
        const pos = posMap[s.sensor_id]; if (!pos) return;
        const c = cal_snap[s.sensor_id] || { txpower: -59, n_factor: 2.5 };
        const rM  = Math.min(25, Math.pow(10, (c.txpower - (-65)) / (10 * Math.max(0.5, c.n_factor))));
        const rPx = (rM * 100 / sW) * CW;
        const sx  = (pos.x_cm / sW) * CW, sy = CH - (pos.y_cm / sH) * CH;
        for (let r = 0; r < ROWS; r++) for (let c2 = 0; c2 < COLS; c2++) {
          const cx = c2 * GPOW + 7.5, cy = r * GPOW + 7.5;
          if (cx < drTlX || cx > drBrX || cy < drTlY || cy > drBrY) continue;
          if (Math.hypot(cx - sx, cy - sy) <= rPx) grid[r * COLS + c2]++;
        }
      });
      let t = 0, k2 = 0;
      for (let r = 0; r < ROWS; r++) for (let c2 = 0; c2 < COLS; c2++) {
        const cx = c2 * GPOW + 7.5, cy = r * GPOW + 7.5;
        if (cx < drTlX || cx > drBrX || cy < drTlY || cy > drBrY) continue;
        t++; if (grid[r * COLS + c2] >= 2) k2++;
      }
      return t > 0 ? (k2 / t) * 100 : 0;
    };
    // Random position within detection rect
    const randPos = () => {
      const margin = 0.07;
      return {
        x_cm: Math.round((dr ? dr.x_min_cm + (dr.x_max_cm - dr.x_min_cm) * (margin + Math.random() * (1 - 2 * margin)) : sW * (margin + Math.random() * (1 - 2 * margin)))),
        y_cm: Math.round((dr ? dr.y_min_cm + (dr.y_max_cm - dr.y_min_cm) * (margin + Math.random() * (1 - 2 * margin)) : sH * (margin + Math.random() * (1 - 2 * margin))))
      };
    };
    // Fixed positions from entrance sensors
    const baseMap = {};
    fixed_snap.forEach(s => { baseMap[s.sensor_id] = { x_cm: s.x_cm, y_cm: s.y_cm }; });
    let best = { ...baseMap }, bestSc = -1;
    free_snap.forEach(s => { best[s.sensor_id] = { x_cm: s.x_cm, y_cm: s.y_cm }; });
    bestSc = score(best);
    let iter = 0; const N = 300;
    const step = () => {
      for (let k = 0; k < 25 && iter < N; k++, iter++) {
        const trial = { ...baseMap };
        free_snap.forEach(s => { trial[s.sensor_id] = randPos(); });
        const sc = score(trial);
        if (sc > bestSc) { bestSc = sc; best = { ...trial }; }
      }
      if (iter < N) { setTimeout(step, 0); return; }
      // Hill climbing
      for (let h = 0; h < 80; h++) {
        let improved = false;
        free_snap.forEach(s => {
          for (let k = 0; k < 30; k++) {
            const trial = { ...best, [s.sensor_id]: randPos() };
            const sc = score(trial);
            if (sc > bestSc) { bestSc = sc; best = { ...trial }; improved = true; }
          }
        });
        if (!improved) break;
      }
      setSuggestedPositions(best);
      setOptimizedScore(Math.round(rawCoverage(best)));
      setOptimizedAniso(anisotropyOf(Object.values(best)));
      setOptimizing(false);
    };
    setTimeout(step, 30);
  }, [sensors, calibrations, dimensions, detectionRect, entranceSensorIds]);

  const applySuggested = async () => {
    if (!suggestedPositions) return;
    for (const [sensorId, pos] of Object.entries(suggestedPositions)) {
      await handlePositionChange(sensorId, pos);
    }
    setSuggestedPositions(null); setOptimizedScore(null);
  };

  const handleFloorplanUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setLoading(true);
    try {
      await floorplanAPI.upload(siteId, fd);
      setFloorplanUrl(floorplanAPI.getUrl(siteId) + '?t=' + Date.now()); setHasFloorplan(true);
      setSuccess('Plan importé');
    } catch { setError('Erreur upload plan'); }
    setLoading(false); e.target.value = '';
  };
  const handleFloorplanDelete = async () => {
    if (!window.confirm('Supprimer le plan ?')) return;
    try { await floorplanAPI.remove(siteId); setFloorplanUrl(null); setHasFloorplan(false); setSuccess('Plan supprimé'); }
    catch { setError('Erreur suppression'); }
  };
  const handlePositionChange = async (sensorId, pos) => {
    try {
      // On ecrit sur l'AFFECTATION de ce site. Si elle est terminee, le capteur
      // — possiblement en service ailleurs — n'est pas touche.
      const res = await api.put(
        '/v1/sites/' + siteId + '/deployed-sensors/' + sensorId + '/position', pos);
      const zone_name = res.data?.zone_name || null;
      setSensors(prev => prev.map(s => s.sensor_id === sensorId
        ? { ...s, ...pos, zone_name: zone_name || s.zone_name } : s));
      const s = sensors.find(x => x.sensor_id === sensorId);
      const label = (s && s.display_name) || sensorId;
      const suffixe = (s && s.ailleurs)
        ? ' — position historique (capteur en service sur ' + s.ailleurs + ')'
        : (zone_name ? ' → ' + zone_name : '');
      setSuccess('Position sauvegardée — ' + label + suffixe);
    } catch { setError('Erreur position'); }
  };
  const handleZoneDrawn = (coords) => { setDrawnCoords(coords); setForm(EMPTY_FORM); setEditingZone(null); setDialogOpen(true); };
  const saveZone = async () => {
    if (!form.name) return; setLoading(true);
    try {
      const payload = { ...drawnCoords, ...form, display_order: form.display_order ? +form.display_order : 0 };
      if (editingZone) {
        await zonesAPI.update(siteId, editingZone.zone_id, payload);
        setZones(prev => prev.map(z => z.zone_id === editingZone.zone_id ? { ...z, ...payload } : z));
      } else {
        const res = await zonesAPI.create(siteId, payload);
        setZones(prev => [...prev, { ...payload, zone_id: res.data.zone_id }]);
      }
      setDialogOpen(false); setDrawnCoords(null); setSuccess(editingZone ? 'Zone modifiée' : 'Zone créée');
    } catch { setError('Erreur zone'); }
    setLoading(false);
  };
  const deleteZone = async (zoneId, name) => {
    if (!window.confirm('Supprimer "' + name + '" ?')) return;
    try { await zonesAPI.delete(siteId, zoneId); setZones(prev => prev.filter(z => z.zone_id !== zoneId)); setSuccess('Zone supprimée'); }
    catch { setError('Erreur suppression'); }
  };
  const openEdit = (zone) => {
    setEditingZone(zone);
    setDrawnCoords({ x_min_cm: zone.x_min_cm, x_max_cm: zone.x_max_cm, y_min_cm: zone.y_min_cm, y_max_cm: zone.y_max_cm });
    setForm({ name: zone.name, color: zone.color, display_order: zone.display_order || '' });
    setDialogOpen(true);
  };
  const saveDimensions = async () => {
    setLoading(true);
    try {
      await api.put('/v1/sites/' + siteId + '/dimensions', { width_cm: +dimForm.width_cm, height_cm: +dimForm.height_cm });
      setDimensions({ width_cm: +dimForm.width_cm, height_cm: +dimForm.height_cm });
      setDimDialog(false); setSuccess('Dimensions sauvegardées');
    } catch { setError('Erreur dimensions'); }
    setLoading(false);
  };

  const canvasReady     = siteId && dimensions.width_cm > 0;
  const placedSensors   = sensors.filter(s => s.x_cm > 0 || s.y_cm > 0);
  const unplacedSensors = sensors.filter(s => !s.x_cm && !s.y_cm);
  const scoreColor = coverageScore === null ? '#999' : coverageScore >= 70 ? '#2E7D32' : coverageScore >= 40 ? '#F57C00' : '#C62828';
  // Geometrie de la disposition ACTUELLE (mise a jour en continu pendant le drag)
  const currentAniso = anisotropyOf(sensors.filter(s => s.x_cm > 0 || s.y_cm > 0)
                                           .map(s => ({ x_cm: s.x_cm, y_cm: s.y_cm })));
  const anisoColor = a => a === null ? '#999' : a <= 2 ? '#2E7D32' : a <= 3 ? '#F57C00' : '#C62828';
  const anisoLabel = a => a === null ? '—' : a >= 99 ? 'alignes' : a.toFixed(1) + 'x';

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#1F4E79">Plan &amp; Zones</Typography>
        <Typography variant="body2" color="text.secondary">
          Configurez l'espace de détection, placez les capteurs et délimitez les zones analytiques
        </Typography>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Barre supérieure */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Boutique</InputLabel>
              <Select value={siteId} label="Boutique" size="small" onChange={e => setSiteId(e.target.value)}>
                {sites.map(s => <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>

            {siteId && (
              <>
                <Button variant="outlined" size="small" onClick={() => setDimDialog(true)}>
                  📐 {dimensions.width_cm || '?'} × {dimensions.height_cm || '?'} cm
                </Button>
                <Button variant="outlined" size="small" component="label" startIcon={<UploadFileIcon />} disabled={loading}>
                  {hasFloorplan ? 'Changer le plan' : 'Importer un plan'}
                  <input type="file" hidden accept="image/*" onChange={handleFloorplanUpload} />
                </Button>
                {hasFloorplan && <Button size="small" color="error" onClick={handleFloorplanDelete}>Supprimer plan</Button>}
                {hasFloorplan && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                    <Tooltip title="Rotation -90°"><IconButton size="small" onClick={() => { const t2 = { ...imgTransform, rotation: (imgTransform.rotation - 90 + 360) % 360 }; setImgTransform(t2); saveFloorplanLayout(siteId, t2, imgRect); }}><RotateLeftIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Rotation +90°"><IconButton size="small" onClick={() => { const t2 = { ...imgTransform, rotation: (imgTransform.rotation + 90) % 360 }; setImgTransform(t2); saveFloorplanLayout(siteId, t2, imgRect); }}><RotateRightIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Couvrir toute la zone">
                      <Button size="small" variant="outlined" onClick={() => { const r = ((imgTransform.rotation % 360) + 360) % 360; const nr = (r === 90 || r === 270) ? { x: (CANVAS_W - CANVAS_H) / 2, y: (CANVAS_H - CANVAS_W) / 2, w: CANVAS_H, h: CANVAS_W } : { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H }; setImgRect(nr); saveFloorplanLayout(siteId, imgTransform, nr); }} sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0, borderColor: '#1565C0', color: '#1565C0' }}>Couvrir</Button>
                    </Tooltip>
                    <Tooltip title="Sauvegarder la disposition">
                      <Button size="small" variant={layoutSaved ? 'contained' : 'outlined'} onClick={() => { saveFloorplanLayout(siteId, imgTransform, imgRect); setLayoutSaved(true); setTimeout(() => setLayoutSaved(false), 2000); }} sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0, bgcolor: layoutSaved ? '#2E7D32' : 'transparent', borderColor: layoutSaved ? '#2E7D32' : '#2E7D32', color: layoutSaved ? '#FFF' : '#2E7D32' }}>{layoutSaved ? '✓ Sauvegardé' : 'Sauvegarder'}</Button>
                    </Tooltip>
                  </Box>
                )}
              </>
            )}

            {tab === 2 && siteId && (
              <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" mr={0.5}>Couleur :</Typography>
                {COLORS.map(color => (
                  <Box key={color} onClick={() => setSelectedColor(color)}
                    sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                      border: selectedColor === color ? '3px solid #333' : '2px solid transparent',
                      transition: 'all 0.15s', '&:hover': { transform: 'scale(1.2)' } }} />
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={tab === 1 ? 9 : 12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                  <Tab label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      📐 Plan
                      {detectionRect && <Chip label="✓" size="small" color="primary" sx={{ height: 16, fontSize: 9 }} />}
                    </Box>
                  } />
                  <Tab label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RouterIcon fontSize="small" /> Capteurs
                      {sensors.length > 0 && <Chip label={sensors.length} size="small" color={unplacedSensors.length > 0 ? 'warning' : 'default'} />}
                    </Box>
                  } />
                  <Tab label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MapIcon fontSize="small" /> Zones
                      {zones.length > 0 && <Chip label={zones.length} size="small" />}
                    </Box>
                  } />
                  <Tab label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MapIcon fontSize="small" /> Frequentation
                    </Box>
                  } />
                </Tabs>

                {/* Toolbar tab 1 */}
                {tab === 1 && canvasReady && placedSensors.length >= 2 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {suggestedPositions ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#FF9800', fontWeight: 700 }}>
                          Suggestion : {optimizedScore}%{coverageScore !== null ? ` (+${Math.max(0, optimizedScore - coverageScore)}%)` : ''}{optimizedAniso !== null ? ` · geo ${anisoLabel(optimizedAniso)}` : ''}
                        </Typography>
                        <Button size="small" variant="contained" onClick={applySuggested}
                          sx={{ bgcolor: '#FF9800', fontSize: 11, py: 0.25, px: 1 }}>Appliquer</Button>
                        <Button size="small" onClick={() => { setSuggestedPositions(null); setOptimizedScore(null); }}
                          sx={{ fontSize: 11, py: 0.25, px: 1 }}>✕</Button>
                      </Box>
                    ) : (
                      <Button size="small" variant="outlined" onClick={runOptimization} disabled={optimizing}
                        sx={{ fontSize: 11, py: 0.25, px: 1, borderColor: '#1565C0', color: '#1565C0' }}>
                        {optimizing ? 'Calcul...' : '⚡ Optimiser'}
                      </Button>
                    )}
                    <FormControlLabel
                      control={<Switch size="small" checked={showCoverage} onChange={e => setShowCoverage(e.target.checked)} />}
                      label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LayersIcon fontSize="small" /><Typography variant="caption">Couverture</Typography></Box>}
                      sx={{ mr: 0 }}
                    />
                    {showCoverage && coverageScore !== null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
                        <Typography variant="caption" color="text.secondary">Triangulation :</Typography>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={coverageScore}
                            sx={{ height: 8, borderRadius: 4, bgcolor: '#EEE', '& .MuiLinearProgress-bar': { bgcolor: scoreColor, borderRadius: 4 } }} />
                        </Box>
                        <Typography variant="caption" fontWeight={700} color={scoreColor}>{coverageScore}%</Typography>
                      </Box>
                    )}
                    {showCoverage && currentAniso !== null && (
                      <Tooltip title={"Anisotropie des capteurs : <=2 sain · 2-3 acceptable · >3 le positionnement lateral se degrade. Une bonne couverture ne garantit pas une bonne geometrie : des capteurs alignes couvrent bien mais trilaterent mal."}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Geometrie :</Typography>
                          <Typography variant="caption" fontWeight={700} color={anisoColor(currentAniso)}>
                            {anisoLabel(currentAniso)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}

                {/* Toolbar tab 0 */}
                {tab === 0 && siteId && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant={planMode === 'detection' ? 'contained' : 'outlined'}
                      onClick={() => setPlanMode('detection')}
                      sx={{ fontSize: 11, py: 0.4, bgcolor: planMode === 'detection' ? '#1565C0' : undefined }}>
                      ⬚ Espace de détection
                    </Button>
                    <Button size="small" variant={planMode === 'entrance' ? 'contained' : 'outlined'}
                      onClick={() => setPlanMode('entrance')}
                      sx={{ fontSize: 11, py: 0.4, bgcolor: planMode === 'entrance' ? '#2E7D32' : undefined, borderColor: '#2E7D32', color: planMode === 'entrance' ? '#FFF' : '#2E7D32' }}>
                      🚪 Entrées
                    </Button>
                    {detectionRect && (
                      <Button size="small" color="error" onClick={() => { setDetectionRect(null); savePlanData(siteId, null, entrances, entranceSensorIds); }}
                        sx={{ fontSize: 11, py: 0.4 }}>✕ Effacer</Button>
                    )}
                  </Box>
                )}
              </Box>

              {!siteId && <Alert severity="info">Sélectionnez une boutique</Alert>}
              {siteId && !canvasReady && <Alert severity="warning">Définissez d'abord les dimensions (bouton 📐)</Alert>}

              {/* Tab 0 — Plan */}
              {canvasReady && tab === 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    {planMode === 'detection' ? 'Glissez pour définir l\'espace de détection · Les capteurs et l\'optimiseur sont contraints à cette zone' : 'Cliquez pour placer une entrée · Cliquez sur E pour la supprimer'}
                  </Typography>
                  <PlanCanvas mode={planMode} detectionRect={detectionRect} entrances={entrances}
                    onDetectionRect={handleDetectionRect} onEntranceToggle={handleEntranceToggle}
                    zones={zones} sensors={sensors} floorplanUrl={floorplanUrl}
                    imgTransform={imgTransform} imgRect={imgRect} dimensions={dimensions} />
                </>
              )}

              {/* Tab 1 — Capteurs */}
              {canvasReady && tab === 1 && (
                <>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    Glissez un capteur · Drag &amp; drop pour repositionner · Clic droit pour retirer
                    {showCoverage && <> · <span style={{color:'#B0B0B0'}}>■ Non couvert</span> <span style={{color:'#FF5722'}}>■ 1 capteur</span> <span style={{color:'#8BC34A'}}>■ ≥2</span> <span style={{color:'#1B5E20'}}>■ ≥3</span></>}
                  </Typography>
                  <SensorCanvas sensors={sensors} dimensions={dimensions} zones={zones}
                    floorplanUrl={floorplanUrl} onPositionChange={handlePositionChange}
                    draggedSensor={draggedSensor} imgTransform={imgTransform} imgRect={imgRect}
                    calibrations={calibrations} showCoverage={showCoverage} onCoverageScore={setCoverageScore}
                    suggestedPositions={suggestedPositions} detectionRect={detectionRect}
                    entrances={entrances} entranceSensorIds={entranceSensorIds} />
                </>
              )}

              {/* Tab 2 — Zones */}
              {tab === 3 && siteId && <CarteFrequentation siteId={siteId} />}

              {canvasReady && tab === 2 && (
                <>
                  <Typography variant="caption" color="text.secondary" mb={1} display="block">
                    Cliquez et glissez pour dessiner une zone analytique
                  </Typography>
                  <ZoneCanvas zones={zones} dimensions={dimensions} onZoneDrawn={handleZoneDrawn}
                    selectedColor={selectedColor} floorplanUrl={floorplanUrl} sensors={sensors}
                    imgTransform={imgTransform} imgRect={imgRect} detectionRect={detectionRect} />
                </>
              )}
            </CardContent>
          </Card>

          {/* Zones list (tab 2) */}
          {canvasReady && tab === 2 && zones.length > 0 && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body2" fontWeight={600} mb={1} color="#1F4E79">Zones définies</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {zones.map(z => (
                    <Chip key={z.zone_id} label={z.name} size="small"
                      onDelete={() => deleteZone(z.zone_id, z.name)} onClick={() => openEdit(z)}
                      sx={{ bgcolor: z.color + '22', borderColor: z.color, border: '1px solid', color: z.color, fontWeight: 600 }} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Entrances list (tab 0) */}
          {canvasReady && tab === 0 && entrances.length > 0 && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body2" fontWeight={600} mb={1} color="#2E7D32">Entrées définies</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {entrances.map((e, i) => (
                    <Chip key={i} label={'E' + (i + 1) + (e.label ? ' — ' + e.label : '')} size="small"
                      onDelete={() => handleEntranceToggle({ remove: i })}
                      sx={{ bgcolor: '#E8F5E9', borderColor: '#2E7D32', border: '1px solid', color: '#2E7D32', fontWeight: 600 }} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Calibration legend (tab 1) */}
          {canvasReady && tab === 1 && showCoverage && placedSensors.length > 0 && (
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 2 }}>
              <CardContent sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>Cercles : trait plein = −65 dBm · pointillés = −75 dBm</Typography>
                {placedSensors.map(s => {
                  const c = calibrations[s.sensor_id] || { txpower: -59, n_factor: 2.5 };
                  const r65 = Math.round(Math.pow(10, (c.txpower - (-65)) / (10 * c.n_factor)));
                  const r75 = Math.round(Math.pow(10, (c.txpower - (-75)) / (10 * c.n_factor)));
                  const isEnt = entranceSensorIds.includes(s.sensor_id);
                  return (
                    <Typography key={s.sensor_id} variant="caption" color="text.secondary" display="block">
                      {isEnt ? '🚪 ' : ''}{s.display_name || s.sensor_id} · n={c.n_factor} → −65dBm≈{r65}m · −75dBm≈{r75}m
                    </Typography>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Panneau capteurs (tab 1) */}
        {tab === 1 && canvasReady && (
          <Grid item xs={12} md={3}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 2, border: unplacedSensors.length > 0 ? '1px solid #FF9800' : undefined }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body2" fontWeight={700} mb={1} color="#1F4E79" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RouterIcon fontSize="small" /> Non placés
                  {unplacedSensors.length > 0 && <Chip label={unplacedSensors.length} size="small" color="warning" sx={{ ml: 'auto' }} />}
                </Typography>
                {unplacedSensors.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">Tous les capteurs sont placés ✓</Typography>
                ) : (
                  <List dense disablePadding>
                    {unplacedSensors.map(s => (
                      <Paper key={s.sensor_id} elevation={1} draggable
                        onDragStart={e => { e.dataTransfer.setData('sensor_id', s.sensor_id); setDraggedSensor(s.sensor_id); }}
                        onDragEnd={() => setDraggedSensor(null)}
                        sx={{ mb: 1, p: 1, cursor: 'grab', borderRadius: 2, border: '1px dashed #CCC', '&:hover': { bgcolor: '#FFF3E0', borderColor: '#FF9800' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DragIndicatorIcon fontSize="small" sx={{ color: '#CCC' }} />
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.is_online ? '#4CAF50' : '#CCC' }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600} fontSize="0.8rem" lineHeight={1.2}>{s.display_name || s.sensor_id}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.68rem', color: '#1565C0', lineHeight: 1.3 }}>{s.sensor_id}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.role}</Typography>
                            {s.ailleurs && <Typography variant="caption" sx={{ display: 'block', color: '#E65100', fontWeight: 600, fontSize: '0.66rem' }}>
                              historique — en service sur {s.ailleurs}
                            </Typography>}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>

            {placedSensors.length > 0 && (
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" fontWeight={700} mb={1} color="#1F4E79" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RouterIcon fontSize="small" /> Placés
                    <Chip label={placedSensors.length} size="small" color="success" sx={{ ml: 'auto' }} />
                  </Typography>
                  <List dense disablePadding>
                    {placedSensors.map((s, i) => {
                      const isEnt = entranceSensorIds.includes(s.sensor_id);
                      return (
                        <React.Fragment key={s.sensor_id}>
                          <ListItem sx={{ px: 0, py: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.is_online ? '#4CAF50' : '#CCC', mr: 1, flexShrink: 0 }} />
                            <ListItemText
                              primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {s.display_name || s.sensor_id}
                                {isEnt && <Chip label="Entrée" size="small" sx={{ height: 14, fontSize: 8, bgcolor: '#E8F5E9', color: '#2E7D32', border: '1px solid #2E7D32' }} />}
                              </Box>}
                              secondary={<>
                                <Box component="span" sx={{ color: '#1565C0', fontWeight: 700 }}>{s.sensor_id}</Box>
                                {' · ' + s.x_cm + 'cm, ' + s.y_cm + 'cm' + (s.zone_name ? ' · ' + s.zone_name : '')}
                                {s.ailleurs && <Box component="span" sx={{ display: 'block', color: '#E65100', fontWeight: 600 }}>
                                  historique — en service sur {s.ailleurs}
                                </Box>}
                              </>}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.8rem' }}
                              secondaryTypographyProps={{ fontSize: '0.72rem', fontFamily: 'monospace' }}
                            />
                            <Tooltip title={isEnt ? 'Retirer marquage entrée' : 'Marquer comme capteur d\'entrée'}>
                              <IconButton size="small" onClick={() => toggleEntranceSensor(s.sensor_id)}
                                sx={{ color: isEnt ? '#2E7D32' : '#CCC' }}>
                                🚪
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remettre non placé">
                              <IconButton size="small" onClick={() => handlePositionChange(s.sensor_id, { x_cm: 0, y_cm: 0 })}>
                                <DeleteIcon fontSize="small" sx={{ color: '#CCC' }} />
                              </IconButton>
                            </Tooltip>
                          </ListItem>
                          {i < placedSensors.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        )}
      </Grid>

      {/* Dialog zone */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingZone ? 'Modifier la zone' : 'Nommer la zone'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {drawnCoords && <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>X {drawnCoords.x_min_cm}→{drawnCoords.x_max_cm} cm | Y {drawnCoords.y_min_cm}→{drawnCoords.y_max_cm} cm</Alert>}
          <TextField fullWidth label="Nom de la zone" required autoFocus value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} sx={{ mb: 2 }} />
          <Typography variant="body2" fontWeight={500} mb={1}>Couleur</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {COLORS.map(color => <Box key={color} onClick={() => setForm(p => ({...p, color}))} sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: color, cursor: 'pointer', border: form.color === color ? '3px solid #333' : '3px solid transparent' }} />)}
          </Box>
          <TextField fullWidth label="Ordre d'affichage (optionnel)" type="number" value={form.display_order} onChange={e => setForm(p => ({...p, display_order: e.target.value}))} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setDialogOpen(false); setDrawnCoords(null); }}>Annuler</Button>
          <Button variant="contained" onClick={saveZone} disabled={loading || !form.name} startIcon={editingZone ? <SaveIcon /> : <AddIcon />} sx={{ bgcolor: '#1F4E79' }}>
            {editingZone ? 'Sauvegarder' : 'Créer la zone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog entrance label */}
      <Dialog open={!!entranceLabelDlg} onClose={() => setEntranceLabelDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Nommer l'entrée</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nom (ex: Entrée principale, Accès parking…)" autoFocus
            value={entranceLabelDlg?.label || ''}
            onChange={e => setEntranceLabelDlg(p => ({...p, label: e.target.value}))}
            onKeyDown={e => { if (e.key === 'Enter') saveEntranceLabel(); }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEntranceLabelDlg(null)}>Passer</Button>
          <Button variant="contained" onClick={saveEntranceLabel} sx={{ bgcolor: '#2E7D32' }}>Valider</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog dimensions */}
      <Dialog open={dimDialog} onClose={() => setDimDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Dimensions de la boutique</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>Dimensions réelles en cm — référence pour la triangulation.</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Largeur (cm)" type="number" value={dimForm.width_cm} onChange={e => setDimForm(p => ({...p, width_cm: e.target.value}))} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Hauteur (cm)" type="number" value={dimForm.height_cm} onChange={e => setDimForm(p => ({...p, height_cm: e.target.value}))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDimDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={saveDimensions} disabled={loading} sx={{ bgcolor: '#1F4E79' }}>Sauvegarder</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
